// src/data/intensiveTeaching.js
// Pre-Marriage Intensive - Teaching Content for Modules 1-10
// Visual Theme: Emerald/Teal with Gold accents

export const INTENSIVE_TEACHING = {
  // ============================================================
  // MODULE 1: KINGDOM MARRIAGE PERSPECTIVE
  // ============================================================
  1: {
    moduleNumber: 1,
    moduleName: "Kingdom Marriage Perspective",
    factorName: "Understanding God's Original Design",
    totalSlides: 10,
    slides: [
      // SLIDE 1 - Title
      {
        type: "title",
        title: "Kingdom Marriage",
        subtitle: "Understanding God's Original Design",
        scripture: "\"Therefore a man shall leave his father and mother and be joined to his wife, and they shall become one flesh.\" — Genesis 2:24",
        highlight: "Marriage did not originate from culture, romance, or tradition. Marriage originated from God."
      },
      // SLIDE 2 - The Problem
      {
        type: "content",
        title: "Why Marriages Struggle",
        points: [
          "Many marriages fail not because couples are bad—but because they operate with the wrong framework.",
          "No one enters a bad marriage at gunpoint. Most failed marriages showed warning signs at the beginning.",
          "Before learning skills (communication, intimacy, conflict), couples must first understand what marriage IS.",
          "The question is not 'How do we stay happy?' but 'What has God designed marriage to be?'"
        ],
        scripture: "\"My people are destroyed for lack of knowledge.\" — Hosea 4:6"
      },
      // SLIDE 3 - Three Models
      {
        type: "content",
        title: "Three Marriage Models",
        points: [
          "🏛️ TRADITIONAL: Culture-driven, male dominance, woman's voice minimized, Scripture misused for control.",
          "⚖️ CONTEMPORARY: Individual rights, no clear leadership, power struggles, marriage viewed as disposable.",
          "👑 KINGDOM: God's purpose at center, covenant not contract, interdependence, mutual honor under divine authority.",
          "Cultural marriage favors men. Contemporary marriage favors women. Kingdom marriage favors GOD'S PURPOSE."
        ],
        scripture: "\"Unless the Lord builds the house, they labor in vain who build it.\" — Psalm 127:1"
      },
      // SLIDE 4 - Covenant vs Contract
      {
        type: "content",
        title: "Covenant vs. Contract",
        points: [
          "A CONTRACT is based on mutual distrust: 'If you break your end, I'm released from mine.'",
          "A COVENANT is based on commitment: 'I will keep my promise regardless of your failures.'",
          "Contracts protect individual rights. Covenants protect the relationship.",
          "God's covenant with Israel was unconditional—and marriage is designed to reflect that same faithfulness."
        ],
        scripture: "\"What God has joined together, let not man separate.\" — Matthew 19:6"
      },
      // SLIDE 5 - Leave and Cleave
      {
        type: "content",
        title: "Leave, Cleave, Become One",
        points: [
          "LEAVE: Marriage creates a new primary family. Parents become secondary, not abandoned but re-ordered.",
          "CLEAVE: 'Joined' means glued together—permanent attachment, not temporary connection.",
          "BECOME ONE: Not just sexual intimacy, but one mission, one direction, one identity before God.",
          "This is not about losing yourself—it's about finding your purpose together."
        ],
        scripture: "\"For this reason a man shall leave his father and mother and be joined to his wife.\" — Genesis 2:24"
      },
      // SLIDE 6 - Jesus on Marriage
      {
        type: "content",
        title: "Jesus Returns to Original Design",
        points: [
          "When Pharisees asked about divorce, Jesus didn't discuss technicalities—He returned to Genesis.",
          "His answer: 'Have you not read that He who made them at the beginning made them male and female?'",
          "When marriage breaks down, the solution is not negotiation—it's realignment with God's design.",
          "Jesus elevates marriage from social arrangement to spiritual reflection of His love for the Church."
        ],
        scripture: "\"So then, they are no longer two but one flesh.\" — Matthew 19:6"
      },
      // SLIDE 7 - Wrong Perspectives
      {
        type: "content",
        title: "Dangerous Marriage Perspectives",
        points: [
          "❌ 'Marriage will fix my loneliness' — Marriage exposes loneliness; it doesn't cure it.",
          "❌ 'Marriage is about getting my needs met' — This is consumer thinking, not covenant thinking.",
          "❌ 'We will figure it out later' — You cannot fix a foundation after building the house.",
          "❌ 'If it doesn't work, divorce is an option' — Exit strategies prevent full commitment."
        ],
        scripture: "\"There is a way that seems right to a man, but its end is the way of death.\" — Proverbs 14:12"
      },
      // SLIDE 8 - Kingdom Perspective
      {
        type: "content",
        title: "Kingdom Marriage Exists To...",
        points: [
          "✅ Fulfill God's purpose — Marriage is not the goal; it's the platform for purpose.",
          "✅ Mature character — Marriage is sanctification bootcamp.",
          "✅ Build legacy — Your marriage impacts generations.",
          "✅ Reflect Christ and the Church — The world should see Jesus in how you love each other."
        ],
        scripture: "\"Husbands, love your wives, just as Christ also loved the church and gave Himself for her.\" — Ephesians 5:25"
      },
      // SLIDE 9 - Reflection
      {
        type: "reflection",
        title: "Heart Check Questions",
        questions: [
          "Why do I want to get married? Is it escape, completion, or calling?",
          "What do I believe marriage should give me? What am I prepared to sacrifice?",
          "Do I view divorce as a backup plan? Am I truly ready for permanent commitment?",
          "Is my perspective on marriage cultural, contemporary, or kingdom-based?"
        ],
        instruction: "Take time to answer these honestly. Unexamined perspectives lead to painful surprises."
      },
      // SLIDE 10 - Commitment
      {
        type: "action",
        title: "Our Declaration",
        challenge: "Verbally declare together: 'We choose to build our marriage as a Kingdom marriage—according to God's design, for God's purpose, with God at the center.'",
        nextStep: "Before proceeding, ensure both partners genuinely embrace this foundation. If resistance exists here, it will surface in every other area.",
        scripture: "\"As for me and my house, we will serve the Lord.\" — Joshua 24:15",
        closing: "A strong foundation determines everything built upon it."
      }
    ]
  },

  // ============================================================
  // MODULE 2: INTIMACY THAT SUSTAINS MARRIAGE
  // ============================================================
  2: {
    moduleNumber: 2,
    moduleName: "Intimacy That Sustains Marriage",
    factorName: "Beyond Romance, Feelings, and Attraction",
    totalSlides: 10,
    slides: [
      // SLIDE 1 - Title
      {
        type: "title",
        title: "Intimacy That Sustains",
        subtitle: "Beyond Romance, Feelings & Sexual Attraction",
        scripture: "\"Can two walk together, unless they are agreed?\" — Amos 3:3",
        highlight: "Many couples feel 'in love' but are not truly intimate. This lack of true intimacy is why many marriages collapse under pressure."
      },
      // SLIDE 2 - Redefining Intimacy
      {
        type: "content",
        title: "Redefining Intimacy",
        points: [
          "Cultural definition reduces intimacy to: sexual frequency, emotional excitement, chemistry, physical attraction.",
          "These are EXPRESSIONS of intimacy—not its foundation.",
          "Kingdom definition: Intimacy is INTENTIONAL CLOSENESS built on trust, safety, and shared life.",
          "True intimacy answers: 'Can I be fully known here and still be safe?'"
        ],
        scripture: "\"This is my beloved, and this is my friend.\" — Song of Solomon 5:16"
      },
      // SLIDE 3 - INTIMATE Framework Part 1
      {
        type: "content",
        title: "The INTIMATE Framework (Part 1)",
        points: [
          "I — INTENTIONALITY: Intimacy doesn't happen naturally. It happens deliberately. Marriage drifts; intimacy must be pursued.",
          "N — NEEDS: Love doesn't remove needs—marriage exposes them. Unmet needs show up as anger, withdrawal, nagging, silence.",
          "T — TRUST: The bedrock. Trust is not assumed—it's built through consistency, transparency, and integrity over time.",
          "Without these three foundations, no amount of romance can sustain a marriage."
        ],
        scripture: "\"The heart of her husband safely trusts her.\" — Proverbs 31:11"
      },
      // SLIDE 4 - INTIMATE Framework Part 2
      {
        type: "content",
        title: "The INTIMATE Framework (Part 2)",
        points: [
          "I — INTERESTS: Marriage must include friendship, not just romance. Relationally bored couples become emotionally distant.",
          "M — MANAGEMENT OF EMOTIONS: Unmanaged emotions destroy intimacy faster than disagreement. Shouting, sarcasm, silent treatment kill connection.",
          "A — ADVANCEMENT TOGETHER: Marriage is designed for joint progress, not parallel lives. Growth must be shared.",
          "T — TOGETHERNESS OVER TIME: Oneness is formed through repeated honesty, forgiveness, and choosing each other."
        ],
        scripture: "\"Two are better than one, because they have a good reward for their labor.\" — Ecclesiastes 4:9"
      },
      // SLIDE 5 - Intentionality Deep Dive
      {
        type: "content",
        title: "Intentionality: The Engine of Intimacy",
        points: [
          "Many couples assume intimacy will 'just happen' because they love each other or pray together.",
          "This assumption is FALSE. You don't drift into closeness—you drift into distance.",
          "Intentionality means: scheduling connection, protecting time, initiating conversations, choosing closeness repeatedly.",
          "Ask yourself: Who initiates connection more? Do we protect time for each other? Is closeness prioritized or optional?"
        ],
        scripture: "\"Let us pursue the things which make for peace and the things by which one may edify another.\" — Romans 14:19"
      },
      // SLIDE 6 - Trust Deep Dive
      {
        type: "content",
        title: "Trust: The Bedrock of Intimacy",
        points: [
          "Trust is destroyed more by inconsistency and secrecy than by big scandals.",
          "Trust weakens when: phones are guarded, finances are hidden, words don't match actions, apologies lack change.",
          "Trust requires: Transparency (open access), Accountability (willingness to answer), Predictability (consistency), Integrity (character over time).",
          "Where trust is weak, intimacy cannot grow—no matter how much love exists."
        ],
        scripture: "\"A good name is to be chosen rather than great riches.\" — Proverbs 22:1"
      },
      // SLIDE 7 - Emotional Management
      {
        type: "content",
        title: "Managing Emotions for Intimacy",
        points: [
          "Most couples don't fight because of issues—they fight because of emotional overflow.",
          "Unmanaged emotions lead to: shouting, silent treatment, sarcasm, escalation, things said that can't be taken back.",
          "Emotional intimacy requires: regulation (controlling reactions), safe expression (sharing without attacking), respectful tone, timing awareness.",
          "He who is slow to anger is better than the mighty—and emotional control is a marriage superpower."
        ],
        scripture: "\"He who is slow to anger is better than the mighty, and he who rules his spirit than he who takes a city.\" — Proverbs 16:32"
      },
      // SLIDE 8 - Friendship Factor
      {
        type: "content",
        title: "Friendship: The Often Missing Ingredient",
        points: [
          "Many couples are sexually connected but relationally bored—creating emotional distance and vulnerability to outside connection.",
          "Marriage without friendship creates: loneliness inside marriage, seeking emotional fulfillment elsewhere, going through motions without joy.",
          "Couples must learn to: talk beyond logistics, laugh together, enjoy shared activities, be curious about each other.",
          "Would you choose your spouse as a friend? If not, this needs attention."
        ],
        scripture: "\"A friend loves at all times.\" — Proverbs 17:17"
      },
      // SLIDE 9 - Reflection
      {
        type: "reflection",
        title: "Intimacy Assessment",
        questions: [
          "Can I be fully known by my partner and still feel safe?",
          "What makes me feel most loved? What makes me feel ignored?",
          "Do we have meaningful conversations beyond daily logistics?",
          "How do I react when overwhelmed—do I shut down or explode?"
        ],
        instruction: "Answer privately first, then share. Vulnerability here builds the intimacy you're discussing."
      },
      // SLIDE 10 - Commitment
      {
        type: "action",
        title: "Our Intimacy Commitment",
        challenge: "Identify one area of the INTIMATE framework that needs the most attention. Commit to one specific action this week to strengthen it.",
        nextStep: "Schedule a weekly 'connection time'—protected, distraction-free time to talk, dream, and be together.",
        scripture: "\"And they were both naked, the man and his wife, and were not ashamed.\" — Genesis 2:25",
        closing: "True intimacy is built day by day, choice by choice, conversation by conversation."
      }
    ]
  },

  // ============================================================
  // MODULE 3: COMMUNICATION & CONFLICT MASTERY
  // ============================================================
  3: {
    moduleNumber: 3,
    moduleName: "Communication & Conflict Mastery",
    factorName: "How Godly Couples Disagree Without Destroying Intimacy",
    totalSlides: 10,
    slides: [
      // SLIDE 1 - Title
      {
        type: "title",
        title: "Communication & Conflict",
        subtitle: "How Godly Couples Disagree Without Destroying Intimacy",
        scripture: "\"A soft answer turns away wrath, but a harsh word stirs up anger.\" — Proverbs 15:1",
        highlight: "Most marriages do not collapse from a single crisis. They collapse from unresolved, poorly handled conflict accumulated over time."
      },
      // SLIDE 2 - The Truth About Conflict
      {
        type: "content",
        title: "The Truth About Conflict",
        points: [
          "MYTH: 'If we love each other, we won't fight.' TRUTH: Loving couples still disagree—they just resolve it well.",
          "Conflict is NORMAL. Unresolved conflict is DESTRUCTIVE.",
          "What determines marital success is HOW conflict is handled, not WHETHER it occurs.",
          "A perfect marriage is not one without issues—it's one where issues are resolved promptly and properly."
        ],
        scripture: "\"Be angry, and do not sin: do not let the sun go down on your wrath.\" — Ephesians 4:26"
      },
      // SLIDE 3 - Sources of Conflict
      {
        type: "content",
        title: "What Actually Causes Marital Conflict",
        points: [
          "💰 MONEY: Scarcity, spending styles, secrecy, different financial values",
          "🛏️ INTIMACY: Frequency expectations, desire mismatch, unspoken needs",
          "👨‍👩‍👧 FAMILY: In-law interference, parenting differences, extended family pressure",
          "💬 COMMUNICATION: Different styles, misinterpretation, unmet needs disguised as other issues"
        ],
        scripture: "\"Where do wars and fights come from among you? Do they not come from your desires for pleasure that war in your members?\" — James 4:1"
      },
      // SLIDE 4 - Fight Languages
      {
        type: "content",
        title: "The Five Fight Languages",
        points: [
          "🕊️ THE COMPROMISER: Seeks peace quickly, avoids tension. Risk: suppressed resentment.",
          "🧠 THE LOGICIAN: Focuses on facts, wants examples. Risk: emotional invalidation.",
          "🔥 THE REACTOR: Expresses emotions outwardly, processes externally. Risk: escalation.",
          "❤️ THE VALIDATOR: Needs to feel heard before solutions. Risk: stalled resolution.",
          "🚪 THE AVOIDER: Withdraws, changes subject, delays. Risk: issues remain unresolved."
        ],
        scripture: "\"Let every man be swift to hear, slow to speak, slow to wrath.\" — James 1:19"
      },
      // SLIDE 5 - Non-Negotiable Rules
      {
        type: "content",
        title: "Non-Negotiable Rules for Godly Conflict",
        points: [
          "🚫 NEVER shout, raise voices, or slam doors",
          "🚫 NEVER use manipulation (silence, tears, withholding affection)",
          "🚫 NEVER attack character—address issues, not identity",
          "🚫 NEVER bring up unrelated past offenses or insist on 'winning'",
          "🚫 NEVER walk out without agreeing to return and resolve"
        ],
        scripture: "\"Do not be overcome by evil, but overcome evil with good.\" — Romans 12:21"
      },
      // SLIDE 6 - Resolution Process Part 1
      {
        type: "content",
        title: "The Godly Resolution Process (Part 1)",
        points: [
          "STEP 1 - CALM DOWN: High emotion shuts down reasoning. Take time to regulate before engaging.",
          "STEP 2 - FOCUS ON THE ISSUE: Attack the problem, not your spouse. Replace 'You never...' with 'I feel overwhelmed when...'",
          "STEP 3 - LISTEN ACTIVELY: Listening is not waiting to reply. Make eye contact, ask clarifying questions, reflect what you heard.",
          "Before speaking, ask: 'Do I understand what my spouse is really saying?'"
        ],
        scripture: "\"A fool vents all his feelings, but a wise man holds them back.\" — Proverbs 29:11"
      },
      // SLIDE 7 - Resolution Process Part 2
      {
        type: "content",
        title: "The Godly Resolution Process (Part 2)",
        points: [
          "STEP 4 - SEEK UNDERSTANDING: Understanding does not require agreement. You can disagree and still say 'I understand why you feel that way.'",
          "STEP 5 - FORGIVE AND COMPROMISE: Marriage is not a courtroom. Fight for agreement, not victory.",
          "STEP 6 - APPLY GOD'S WORD: When opinions clash, Scripture becomes the final authority. If Scripture is silent, ask 'What does love require here?'",
          "Resolution means both feel heard, issues are addressed, and relationship is restored."
        ],
        scripture: "\"If it is possible, as much as depends on you, live peaceably with all men.\" — Romans 12:18"
      },
      // SLIDE 8 - Common Mistakes
      {
        type: "content",
        title: "Conflict Mistakes That Destroy Marriage",
        points: [
          "😶 STONEWALLING: Complete emotional withdrawal—shuts partner out entirely",
          "🔄 KEEPING SCORE: 'You did this last time...' creates prosecution, not partnership",
          "📣 CHARACTER ATTACKS: 'You're so selfish' vs 'This decision feels selfish to me'",
          "⏰ DELAYED RESOLUTION: Issues that linger become resentment that poisons everything"
        ],
        scripture: "\"The beginning of strife is like releasing water; therefore stop contention before a quarrel starts.\" — Proverbs 17:14"
      },
      // SLIDE 9 - Reflection
      {
        type: "reflection",
        title: "Conflict Self-Assessment",
        questions: [
          "What is my default fight language? How does it clash with my partner's?",
          "What triggers me most during conflict? What patterns do I need to change?",
          "How safe is my spouse with me when we disagree?",
          "Do I fight to win or fight to resolve and restore?"
        ],
        instruction: "Your answers reveal patterns. Patterns can be changed with awareness and intentionality."
      },
      // SLIDE 10 - Commitment
      {
        type: "action",
        title: "Our Communication Covenant",
        challenge: "Together, establish 3 'rules of engagement' for conflict in your marriage. Write them down and commit to them.",
        nextStep: "Practice the godly resolution process on a small disagreement this week. Master small conflicts before big ones arise.",
        scripture: "\"Let no corrupt word proceed out of your mouth, but what is good for necessary edification, that it may impart grace to the hearers.\" — Ephesians 4:29",
        closing: "How you fight determines whether conflict builds or destroys your marriage."
      }
    ]
  },

  // ============================================================
  // MODULE 4: TRUST, TRANSPARENCY & ACCOUNTABILITY
  // ============================================================
  4: {
    moduleNumber: 4,
    moduleName: "Trust, Transparency & Accountability",
    factorName: "Building Emotional Safety and Covenant Integrity",
    totalSlides: 10,
    slides: [
      // SLIDE 1 - Title
      {
        type: "title",
        title: "Trust, Transparency & Accountability",
        subtitle: "Building Emotional Safety and Covenant Integrity",
        scripture: "\"The heart of her husband safely trusts her; so he will have no lack of gain.\" — Proverbs 31:11",
        highlight: "Love cannot thrive where trust is weak. Intimacy cannot grow where transparency is missing. Marriage cannot endure where accountability is absent."
      },
      // SLIDE 2 - Core Truth
      {
        type: "content",
        title: "The Foundation of Trust",
        points: [
          "Trust is not assumed in marriage—it is EARNED, sustained, and protected.",
          "Trust answers the question: 'Am I safe with you—emotionally, financially, spiritually, and relationally?'",
          "MYTH: 'If you love me, you should trust me automatically.'",
          "TRUTH: Trust grows from consistent character, not verbal assurance."
        ],
        scripture: "\"He who is faithful in what is least is faithful also in much.\" — Luke 16:10"
      },
      // SLIDE 3 - Four Dimensions
      {
        type: "content",
        title: "The Four Dimensions of Trust",
        points: [
          "❤️ EMOTIONAL TRUST: Can I express feelings without fear? Are my vulnerabilities protected?",
          "👥 RELATIONAL TRUST: Are boundaries with others clear? Are past relationships truly closed?",
          "💰 FINANCIAL TRUST: Are finances transparent? Are debts disclosed? Are decisions mutual?",
          "🙏 SPIRITUAL TRUST: Is God honored together? Is spiritual leadership present and sincere?"
        ],
        scripture: "\"Am I safe with you?\" — The core question trust answers"
      },
      // SLIDE 4 - Trust Breakers
      {
        type: "content",
        title: "How Trust Gets Destroyed",
        points: [
          "😶 Feelings are mocked or minimized",
          "⚔️ Past confessions are weaponized during arguments",
          "📱 Private conversations are hidden from spouse",
          "💳 Money is concealed—secret accounts, hidden spending",
          "🚪 Words don't match actions—promises broken repeatedly"
        ],
        scripture: "\"Catch us the foxes, the little foxes that spoil the vines.\" — Song of Solomon 2:15"
      },
      // SLIDE 5 - Transparency
      {
        type: "content",
        title: "Transparency: The Practice That Protects Trust",
        points: [
          "In marriage, there are no private lives—only private moments.",
          "Transparency is not control—it is MUTUAL ACCESS for safety.",
          "📱 Phones and digital life—open and accessible",
          "💵 Finances and spending—fully disclosed",
          "⏰ Time and schedules—known to each other",
          "💬 Emotional connections—shared, not hidden"
        ],
        scripture: "\"Therefore, putting away lying, 'Let each one of you speak truth with his neighbor,' for we are members of one another.\" — Ephesians 4:25"
      },
      // SLIDE 6 - Secrecy Warning
      {
        type: "content",
        title: "A Hard Truth: Secrecy Precedes Betrayal",
        points: [
          "Most major betrayals begin with SMALL secrecy.",
          "The progression: Justification → Emotional distance → Boundary erosion → Betrayal",
          "'It's harmless' → 'We're just friends' → 'Nothing is happening' → Destruction",
          "If you're hiding it from your spouse, ask yourself WHY.",
          "Secrecy is the enemy of intimacy."
        ],
        scripture: "\"Nothing is covered up that will not be revealed, or hidden that will not be known.\" — Luke 12:2"
      },
      // SLIDE 7 - Accountability
      {
        type: "content",
        title: "Accountability: God's Safety Net",
        points: [
          "Marriage is strongest when: God is the first cord, the couple is second, accountability is third.",
          "Couples without accountability are vulnerable during conflict, temptation, transition, and exhaustion.",
          "Healthy accountability includes: Spiritual mentors, pastoral oversight, trusted counselors, mature couples",
          "🚩 RED FLAG: 'Nobody can tell me what to do.' — This mindset is dangerous in marriage."
        ],
        scripture: "\"A threefold cord is not quickly broken.\" — Ecclesiastes 4:12"
      },
      // SLIDE 8 - Covenant Boundaries
      {
        type: "content",
        title: "Non-Negotiable Covenant Boundaries",
        points: [
          "👫 OPPOSITE-SEX: No secret communication. No emotional intimacy outside marriage.",
          "👨‍👩‍👧 EXTENDED FAMILY: Marriage takes priority. Parents do not arbitrate conflicts.",
          "📱 DIGITAL: Transparency with devices. No hidden accounts.",
          "💭 EMOTIONAL: No triangulation. No third-party emotional dependence."
        ],
        scripture: "\"A man shall leave his father and mother and be joined to his wife.\" — Genesis 2:24"
      },
      // SLIDE 9 - Reflection
      {
        type: "reflection",
        title: "Trust & Transparency Audit",
        questions: [
          "Is there anything my partner does not know that affects our relationship?",
          "Do I feel emotionally safe expressing concerns?",
          "Am I defensive when asked questions, or do I welcome inquiry?",
          "Are my boundaries clear and consistent?",
          "Who holds us accountable as a couple?"
        ],
        instruction: "Answer honestly. Trust cannot be built on hidden things. What is revealed can be healed."
      },
      // SLIDE 10 - Commitment
      {
        type: "action",
        title: "Our Trust Covenant",
        challenge: "Commit together: 'We will have no secret life from each other. We will walk in transparency, welcome accountability, and protect our covenant with honesty.'",
        nextStep: "Exchange phone passwords today. Open all accounts to each other. Identify one couple or mentor who can hold you accountable.",
        scripture: "\"Therefore, confess your sins to one another and pray for one another, that you may be healed.\" — James 5:16",
        closing: "Trust is the oxygen of marriage. Build it, protect it, never take it for granted."
      }
    ]
  },

  // ============================================================
  // MODULE 5: EXPECTATIONS, ROLES & GENDER NEEDS
  // ============================================================
  5: {
    moduleNumber: 5,
    moduleName: "Expectations, Roles & Gender Needs",
    factorName: "Understanding What Men and Women Truly Need in Marriage",
    totalSlides: 10,
    slides: [
      // SLIDE 1 - Title
      {
        type: "title",
        title: "Expectations, Roles & Gender Needs",
        subtitle: "Understanding What Men and Women Truly Need in Marriage",
        scripture: "\"Husbands, love your wives... Let the wife see that she respects her husband.\" — Ephesians 5:25, 33",
        highlight: "Marriage does not fail because spouses are unloving. It fails because spouses are loving INCORRECTLY."
      },
      // SLIDE 2 - The Problem
      {
        type: "content",
        title: "Why Couples Miss Each Other",
        points: [
          "Many couples sincerely love each other but: miss each other emotionally, feel unappreciated, feel unseen or unsafe.",
          "A woman loves a man the way SHE wants to be loved.",
          "A man loves a woman the way HE wants to be loved.",
          "Both feel unappreciated. Both feel exhausted.",
          "Love must be expressed in the form the RECEIVER understands."
        ],
        scripture: "\"Let each one of you love his wife as himself, and let the wife see that she respects her husband.\" — Ephesians 5:33"
      },
      // SLIDE 3 - God's Design for Roles
      {
        type: "content",
        title: "God's Design for Roles",
        points: [
          "Ephesians 5 does not establish superiority—it establishes STRUCTURE and RESPONSIBILITY.",
          "Submission is alignment under mission—not inferiority.",
          "Leadership is sacrificial—not authoritarian.",
          "Love is costly—not convenient.",
          "Neither is man independent of woman, nor woman independent of man, in the Lord."
        ],
        scripture: "\"Submitting to one another in the fear of God.\" — Ephesians 5:21"
      },
      // SLIDE 4 - What Men Need
      {
        type: "content",
        title: "What Men Primarily Need",
        points: [
          "✅ ACCEPTANCE: Home is meant to be where acceptance is unconditional. Affirm identity, not just achievement.",
          "✅ RESPECT: Honor, trust, confidence, safety. Many men interpret disrespect as lack of love.",
          "✅ ADMIRATION: Men desire to matter. 'Do I make a difference to you?' Admiration fuels motivation and sacrifice.",
          "🚫 Disrespect includes: Public criticism, sarcasm, dismissive tone, controlling language."
        ],
        scripture: "\"Let the wife see that she respects her husband.\" — Ephesians 5:33"
      },
      // SLIDE 5 - What Women Need
      {
        type: "content",
        title: "What Women Primarily Need",
        points: [
          "❤️ LOVE (As Care): Not abstract—experienced. Affection, tenderness, thoughtfulness, consistency.",
          "🛡️ SECURITY: Emotional (feelings heard), Relational (she's the only one), Spiritual (he can lead and pray).",
          "👀 ATTENTION & AFFIRMATION: Attention communicates value. Affirmation communicates desire.",
          "Lack of attention is often interpreted as: emotional abandonment, loss of love, rejection."
        ],
        scripture: "\"Husbands, love your wives, just as Christ also loved the church and gave Himself for her.\" — Ephesians 5:25"
      },
      // SLIDE 6 - Role Confusion
      {
        type: "content",
        title: "Role Confusion: A Modern Challenge",
        points: [
          "🏛️ Traditional model: Dominance — Often abusive, woman's voice silenced.",
          "⚖️ Contemporary model: Competition — No clear leadership, power struggles.",
          "👑 Kingdom model: Interdependence — Clear structure, sacrificial love, mutual honor.",
          "Marriage is not superiority, equality wars, or independence.",
          "Marriage is INTERDEPENDENCE under God's purpose."
        ],
        scripture: "\"Neither is man independent of woman, nor woman independent of man, in the Lord.\" — 1 Corinthians 11:11"
      },
      // SLIDE 7 - Loving Wisely
      {
        type: "content",
        title: "Loving Wisely, Not Just Sincerely",
        points: [
          "Sincerity without wisdom leads to frustration.",
          "Give what THEY need, not what YOU need.",
          "Men: She needs to FEEL loved through time, words, presence.",
          "Women: He needs to FEEL respected through honor, admiration, support.",
          "Ask: 'What makes you feel most valued?' — Then DO that."
        ],
        scripture: "\"Let each of you look out not only for his own interests, but also for the interests of others.\" — Philippians 2:4"
      },
      // SLIDE 8 - Warning Signs
      {
        type: "content",
        title: "Warning Signs to Address",
        points: [
          "🚩 A man feels disrespected but cannot articulate why.",
          "🚩 A woman feels unloved despite effort.",
          "🚩 One partner feels emotionally unsafe.",
          "🚩 Role expectations are unspoken or rigidly defended.",
          "🚩 'I do everything, yet it's not enough.'",
          "These require intentional reframing, not argument."
        ],
        scripture: "\"Be kindly affectionate to one another with brotherly love, in honor giving preference to one another.\" — Romans 12:10"
      },
      // SLIDE 9 - Reflection
      {
        type: "reflection",
        title: "Expectation Alignment",
        questions: [
          "What makes me feel most valued?",
          "What makes me feel disrespected or unloved?",
          "What expectations do I carry silently?",
          "What assumptions do I make about my spouse?",
          "Am I loving my partner my way or their way?"
        ],
        instruction: "Answer privately first, then share. Unspoken expectations become painful surprises after marriage."
      },
      // SLIDE 10 - Commitment
      {
        type: "action",
        title: "Our Roles Commitment",
        challenge: "Write down your top 5 expectations for marriage. Share them without judgment. Discuss how you can meet each other's needs intentionally.",
        nextStep: "Study Ephesians 5:21-33 together. Discuss what biblical marriage looks like for YOUR unique partnership.",
        scripture: "\"Be kind to one another, tenderhearted, forgiving one another, even as God in Christ forgave you.\" — Ephesians 4:32",
        closing: "Love wisely. Honor differences. Meet each other's needs. Align your roles according to God's design."
      }
    ]
  },

  // ============================================================
  // MODULE 6: READINESS, RED FLAGS & CHECKPOINTS
  // ============================================================
  6: {
    moduleNumber: 6,
    moduleName: "Readiness, Red Flags & Checkpoints",
    factorName: "Discerning Maturity, Alignment, and Long-Term Sustainability",
    totalSlides: 10,
    slides: [
      // SLIDE 1 - Title
      {
        type: "title",
        title: "Readiness, Red Flags & Checkpoints",
        subtitle: "Discerning Maturity, Alignment, and Long-Term Sustainability",
        scripture: "\"The end of a thing is better than its beginning.\" — Ecclesiastes 7:8",
        highlight: "Marriage is designed to improve with time, not deteriorate. If the beginning is already unstable, the ending will be painful."
      },
      // SLIDE 2 - Core Truth
      {
        type: "content",
        title: "What Determines Readiness",
        points: [
          "Readiness is NOT determined by: Age, sexual attraction, loneliness, or pressure from family.",
          "Readiness IS determined by: Character, perspective, and capacity.",
          "No one enters a bad marriage at gunpoint. Most failed marriages showed warning signs at the beginning.",
          "What is tolerated BEFORE marriage becomes suffered AFTER marriage."
        ],
        scripture: "\"The prudent sees danger and hides himself, but the simple go on and suffer for it.\" — Proverbs 22:3"
      },
      // SLIDE 3 - Seven Checkpoints (Part 1)
      {
        type: "content",
        title: "The Seven Premarital Checkpoints (1-4)",
        points: [
          "1️⃣ PERSPECTIVE: What does this person believe marriage is for? Exit strategies = not ready.",
          "2️⃣ MUTUALITY: Are BOTH partners emotionally invested? Marriage cannot be carried by one person.",
          "3️⃣ TIME: Time exposes anger patterns, reveals integrity, tests consistency. Never rush.",
          "4️⃣ FRIENDSHIP: Can you talk, laugh, enjoy time together? Relationship sustained only by sex is weak."
        ],
        scripture: "\"Can two walk together, unless they are agreed?\" — Amos 3:3"
      },
      // SLIDE 4 - Seven Checkpoints (Part 2)
      {
        type: "content",
        title: "The Seven Premarital Checkpoints (5-7)",
        points: [
          "5️⃣ COMPASSION: Does this person have a soft spot for me? Empathy during stress, care during sickness.",
          "6️⃣ COMMITMENT: Is this person faithful in small things? Chronic lateness, frequent excuses = warning.",
          "7️⃣ ACCOUNTABILITY: Does this person submit to authority? 'Nobody can tell me what to do' = dangerous."
        ],
        scripture: "\"He who is faithful in what is least is faithful also in much.\" — Luke 16:10"
      },
      // SLIDE 5 - Red Flags
      {
        type: "content",
        title: "Red Flags That Must Not Be Ignored",
        points: [
          "🚩 Emotional manipulation or threats of breakup",
          "🚩 Chronic disrespect or dishonesty",
          "🚩 Sexual pressure without discipline",
          "🚩 Lack of repentance when wrong",
          "🚩 Patterned irresponsibility",
          "Red flags do not disappear after marriage. They MULTIPLY."
        ],
        scripture: "\"Bad company corrupts good character.\" — 1 Corinthians 15:33"
      },
      // SLIDE 6 - Sexual Discipline
      {
        type: "content",
        title: "Sexual Discipline & Readiness",
        points: [
          "Sexual self-control BEFORE marriage predicts fidelity AFTER marriage.",
          "Lack of discipline before marriage often leads to infidelity after.",
          "Marriage does NOT cure lust. Discipline must be built, not assumed.",
          "MYTH: 'We will stop after we marry.' TRUTH: Habit does not die at the altar."
        ],
        scripture: "\"For this is the will of God, your sanctification: that you should abstain from sexual immorality.\" — 1 Thessalonians 4:3"
      },
      // SLIDE 7 - Dangerous Assumptions
      {
        type: "content",
        title: "Dangerous Pre-Marriage Assumptions",
        points: [
          "❌ 'Marriage will fix my loneliness' — Marriage exposes loneliness; it doesn't cure it.",
          "❌ 'They will change after we marry' — Patterns continue; they rarely transform.",
          "❌ 'We're already doing it, marriage will sanctify it' — Habit does not reset with vows.",
          "❌ 'Divorce is my backup plan' — Exit strategies prevent full commitment."
        ],
        scripture: "\"Do not be deceived, God is not mocked; for whatever a man sows, that he will also reap.\" — Galatians 6:7"
      },
      // SLIDE 8 - When to Pause
      {
        type: "content",
        title: "When to Pause or Delay",
        points: [
          "⏸️ Fear drives decisions instead of peace.",
          "⏸️ Pressure overrides discernment.",
          "⏸️ Red flags are minimized or excused.",
          "⏸️ One partner feels unsafe to speak.",
          "⏸️ Sexual compromise continues without repentance.",
          "Marriage should be entered with CLARITY, not confusion."
        ],
        scripture: "\"Wait for the LORD; be strong, and let your heart take courage.\" — Psalm 27:14"
      },
      // SLIDE 9 - Reflection
      {
        type: "reflection",
        title: "Readiness Self-Assessment",
        questions: [
          "Why do I want to get married NOW?",
          "What am I hoping marriage will FIX?",
          "How do I handle conflict and disappointment?",
          "Am I emotionally stable WITHOUT a relationship?",
          "Do I submit to spiritual authority?"
        ],
        instruction: "Be brutally honest. These questions are not meant to discourage—they're meant to PROTECT."
      },
      // SLIDE 10 - Commitment
      {
        type: "action",
        title: "Our Readiness Declaration",
        challenge: "Declare together: 'We choose wisdom over urgency, clarity over pressure, obedience over emotion, and readiness over convenience.'",
        nextStep: "Review any red flags identified. Create a plan to address them BEFORE setting a wedding date.",
        scripture: "\"Unless the LORD builds the house, they labor in vain who build it.\" — Psalm 127:1",
        closing: "A delayed wedding is a minor inconvenience. A failed marriage is a major tragedy. Choose wisdom."
      }
    ]
  },

  // ============================================================
  // MODULE 7: PURPOSE, CALLING & KINGDOM PARTNERSHIP
  // ============================================================
  7: {
    moduleNumber: 7,
    moduleName: "Purpose, Calling & Kingdom Partnership",
    factorName: "Building a Marriage That Serves God's Assignment",
    totalSlides: 10,
    slides: [
      // SLIDE 1 - Title
      {
        type: "title",
        title: "Purpose, Calling & Kingdom Partnership",
        subtitle: "Building a Marriage That Serves God's Assignment",
        scripture: "\"There are many plans in a man's heart, nevertheless the Lord's counsel—that will stand.\" — Proverbs 19:21",
        highlight: "Marriage is not the purpose. Marriage is a PLATFORM for purpose. God does not join two lives randomly—He joins them intentionally for assignment."
      },
      // SLIDE 2 - Core Truth
      {
        type: "content",
        title: "Why Purpose Matters in Marriage",
        points: [
          "Many couples struggle because they ask: 'How do we stay happy?' instead of 'What has God called us to build?'",
          "Marriage without purpose leads to: boredom, power struggles, emotional dissatisfaction, directionless conflict.",
          "God did not give Adam a wife because Adam was lonely—Adam already had God's presence and purpose.",
          "Marriage came to SUPPORT purpose, not replace it."
        ],
        scripture: "\"And the Lord God said, 'It is not good that man should be alone; I will make him a helper comparable to him.'\" — Genesis 2:18"
      },
      // SLIDE 3 - Kingdom Partnership Defined
      {
        type: "content",
        title: "What Kingdom Partnership Is",
        points: [
          "✅ Two believers under God's authority",
          "✅ Aligned toward God's mission",
          "✅ Serving something bigger than themselves",
          "❌ NOT one partner carrying all vision",
          "❌ NOT marriage built solely around lifestyle",
          "❌ NOT using marriage for status or convenience"
        ],
        scripture: "\"A threefold cord is not quickly broken.\" — Ecclesiastes 4:12"
      },
      // SLIDE 4 - Complementary Calling
      {
        type: "content",
        title: "Aligning Calling Without Competing",
        points: [
          "Common error: Assuming only one person can have a calling, or one must dominate.",
          "This creates: Suppression, competition, resentment.",
          "KINGDOM TRUTH: Calling in marriage is COMPLEMENTARY, not competitive.",
          "Each partner brings: Strengths, perspective, grace, capacity.",
          "The body has many members—not all have the same function, but all are one."
        ],
        scripture: "\"For as we have many members in one body, but all the members do not have the same function, so we, being many, are one body in Christ.\" — Romans 12:4-5"
      },
      // SLIDE 5 - Husband's Role in Purpose
      {
        type: "content",
        title: "The Husband's Role in Kingdom Partnership",
        points: [
          "Kingdom leadership is: Sacrificial, vision-bearing, servant-hearted.",
          "It is NOT: Control, dominance, or entitlement.",
          "Leadership is demonstrated by SELF-GIVING, not authority-asserting.",
          "The husband leads by laying down his life—not by demanding his way.",
          "He carries vision while empowering his wife's calling."
        ],
        scripture: "\"Husbands, love your wives, just as Christ also loved the church and gave Himself for her.\" — Ephesians 5:25"
      },
      // SLIDE 6 - Wife's Role in Purpose
      {
        type: "content",
        title: "The Wife's Role in Kingdom Partnership",
        points: [
          "The word 'helper' (ezer) does not mean subordinate.",
          "The same Hebrew word is used to describe GOD helping Israel.",
          "The helper supplies what is MISSING, not what is inferior.",
          "A wife: Strengthens vision, provides insight, sustains momentum, protects direction.",
          "Without help, vision collapses."
        ],
        scripture: "\"I will make him a helper comparable to him.\" — Genesis 2:18"
      },
      // SLIDE 7 - When Purpose Is Missing
      {
        type: "content",
        title: "Warning Signs: Purpose Is Missing",
        points: [
          "🚩 Chronic boredom despite comfort",
          "🚩 Endless arguments over trivial issues",
          "🚩 Excessive focus on material things",
          "🚩 Emotional emptiness despite having 'everything'",
          "🚩 Feeling like roommates instead of partners",
          "Marriage without mission becomes SELF-CONSUMING."
        ],
        scripture: "\"But seek first the kingdom of God and His righteousness, and all these things shall be added to you.\" — Matthew 6:33"
      },
      // SLIDE 8 - Building Shared Vision
      {
        type: "content",
        title: "Building Shared Vision Together",
        points: [
          "🙏 PRAY TOGETHER: Seek God's direction as a couple, not just individually.",
          "💬 TALK ABOUT CALLING: What burns in your heart? What grieves you?",
          "📝 WRITE IT DOWN: Habakkuk 2:2 — Write the vision and make it plain.",
          "🤝 ALIGN CALENDARS: Your schedule reveals your priorities.",
          "🌱 SERVE TOGETHER: Find ministry you can do as a couple."
        ],
        scripture: "\"Where there is no vision, the people perish.\" — Proverbs 29:18"
      },
      // SLIDE 9 - Reflection
      {
        type: "reflection",
        title: "Purpose Alignment Exercise",
        questions: [
          "What do I believe God has called me to do?",
          "How does marriage support or challenge that calling?",
          "What burden or passion do I carry for God's work?",
          "Where do our callings intersect?",
          "What could God build through us TOGETHER?"
        ],
        instruction: "Answer privately first, then discuss. Discovering shared purpose transforms marriage from survival to significance."
      },
      // SLIDE 10 - Commitment
      {
        type: "action",
        title: "Our Kingdom Partnership Covenant",
        challenge: "Declare together: 'We commit to building a marriage that serves God's purpose, aligns with His will, supports each other's calling, and advances His kingdom through our union.'",
        nextStep: "Write a one-page 'Marriage Mission Statement' this week. What is God calling you to build together?",
        scripture: "\"For we are His workmanship, created in Christ Jesus for good works, which God prepared beforehand that we should walk in them.\" — Ephesians 2:10",
        closing: "Your marriage exists for more than your happiness. It exists for His glory and the world's healing."
      }
    ]
  },

  // ============================================================
  // MODULE 8: SEXUAL INTEGRITY, DISCIPLINE & FIDELITY
  // ============================================================
  8: {
    moduleNumber: 8,
    moduleName: "Sexual Integrity, Discipline & Fidelity",
    factorName: "Building Self-Control Before Marriage and Faithfulness After",
    totalSlides: 10,
    slides: [
      // SLIDE 1 - Title
      {
        type: "title",
        title: "Sexual Integrity, Discipline & Fidelity",
        subtitle: "Building Self-Control Before Marriage and Faithfulness After",
        scripture: "\"Marriage is honorable among all, and the bed undefiled; but fornicators and adulterers God will judge.\" — Hebrews 13:4",
        highlight: "Marriage does not remove temptation. Marriage REVEALS capacity for self-control. Patterns formed before marriage usually continue after."
      },
      // SLIDE 2 - Core Truth
      {
        type: "content",
        title: "The Truth About Sexual Desire",
        points: [
          "Sex is powerful—but power without discipline is destructive.",
          "Sexual desire is GOD-GIVEN. Sexual discipline is GOD-REQUIRED.",
          "DANGEROUS ASSUMPTIONS: 'Marriage will cure lust.' 'Marriage will reduce temptation.' 'Marriage will automatically produce fidelity.'",
          "These assumptions are FALSE. Discipline must be BUILT, not assumed."
        ],
        scripture: "\"For this is the will of God, your sanctification: that you should abstain from sexual immorality.\" — 1 Thessalonians 4:3"
      },
      // SLIDE 3 - God's Design for Sex
      {
        type: "content",
        title: "God's Design for Sex",
        points: [
          "Sex was designed to function within: Covenant, Safety, Commitment, Exclusivity.",
          "INSIDE covenant, sex produces: Bonding with protection, attachment with security, pleasure with responsibility.",
          "OUTSIDE covenant, sex produces: Bonding without protection, attachment without security, pleasure without responsibility.",
          "God honors sex in marriage and judges sexual sin outside it—not out of cruelty, but PROTECTION."
        ],
        scripture: "\"And they were both naked, the man and his wife, and were not ashamed.\" — Genesis 2:25"
      },
      // SLIDE 4 - Discipline Before Marriage
      {
        type: "content",
        title: "Why Discipline Must Come BEFORE Marriage",
        points: [
          "Marriage does not CREATE discipline. Marriage REVEALS discipline.",
          "Patterns formed before marriage continue after.",
          "Most cases of adultery, pornography addiction, and emotional affairs can be traced back to UNDISCIPLINED sexuality before marriage.",
          "The key phrase: 'Know HOW to possess your own vessel' — discipline is LEARNED."
        ],
        scripture: "\"That each of you should know how to possess his own vessel in sanctification and honor.\" — 1 Thessalonians 4:4"
      },
      // SLIDE 5 - The Myth Exposed
      {
        type: "content",
        title: "The Myth: 'We Will Stop After We Marry'",
        points: [
          "DANGEROUS ASSUMPTION: 'If we are already doing it, marriage will sanctify it.'",
          "TRUTH: Habit does not die at the altar.",
          "Desire does not disappear with vows.",
          "Marriage does not reset character.",
          "What you SOW before marriage, you will REAP after marriage."
        ],
        scripture: "\"Do not be deceived, God is not mocked; for whatever a man sows, that he will also reap.\" — Galatians 6:7"
      },
      // SLIDE 6 - Fidelity Beyond Physical
      {
        type: "content",
        title: "Fidelity: More Than Physical Faithfulness",
        points: [
          "Fidelity is not only about bodies—it begins with hearts and minds.",
          "Jesus said looking with lust is adultery of the HEART.",
          "Infidelity often begins with: Emotional openness to others, digital secrecy, justified 'friendships', unchecked imagination.",
          "Guard your heart—for out of it spring the issues of life."
        ],
        scripture: "\"Whoever looks at a woman to lust for her has already committed adultery with her in his heart.\" — Matthew 5:28"
      },
      // SLIDE 7 - Modern Threats
      {
        type: "content",
        title: "Modern Threats to Fidelity",
        points: [
          "📱 PORNOGRAPHY: Rewires desire, distorts expectations, undermines intimacy, trains secrecy.",
          "💬 DIGITAL & EMOTIONAL AFFAIRS: Late-night chats, hidden messages, emotional validation outside marriage.",
          "🚧 BOUNDARY EROSION: 'It's harmless,' 'We're just friends,' 'Nothing is happening.'",
          "These are ENTRY POINTS, not harmless behaviors."
        ],
        scripture: "\"Keep your heart with all diligence, for out of it spring the issues of life.\" — Proverbs 4:23"
      },
      // SLIDE 8 - Building Integrity
      {
        type: "content",
        title: "Building Sexual Integrity NOW",
        points: [
          "✅ Agree on clear PHYSICAL BOUNDARIES.",
          "✅ AVOID triggering environments and situations.",
          "✅ Establish ACCOUNTABILITY relationships.",
          "✅ Practice HONEST CONFESSION when you fall.",
          "✅ GUARD your eyes, mind, and devices.",
          "Guarding is ACTIVE—not passive."
        ],
        scripture: "\"Flee also youthful lusts; but pursue righteousness, faith, love, peace.\" — 2 Timothy 2:22"
      },
      // SLIDE 9 - Reflection
      {
        type: "reflection",
        title: "Sexual Integrity Assessment",
        questions: [
          "Do I struggle with sexual discipline?",
          "Have I normalized compromise?",
          "Are there hidden habits or addictions?",
          "Do I have accountability?",
          "Am I willing to change patterns NOW?"
        ],
        instruction: "Be completely honest. What is hidden will eventually surface. What is confessed can be healed."
      },
      // SLIDE 10 - Commitment
      {
        type: "action",
        title: "Our Purity Covenant",
        challenge: "Declare together: 'We commit to honoring God with our bodies, practicing discipline before marriage, protecting fidelity after marriage, and guarding our covenant with holiness.'",
        nextStep: "Identify one accountability partner each. Share your boundaries. Check in weekly until the wedding and beyond.",
        scripture: "\"Flee sexual immorality. Every sin that a man does is outside the body, but he who commits sexual immorality sins against his own body.\" — 1 Corinthians 6:18",
        closing: "Sexual self-control before marriage predicts fidelity after marriage. Build the discipline now."
      }
    ]
  },

  // ============================================================
  // MODULE 9: ENDURANCE, FORGIVENESS & RESILIENCE
  // ============================================================
  9: {
    moduleNumber: 9,
    moduleName: "Endurance, Forgiveness & Resilience",
    factorName: "How Godly Marriages Survive Seasons of Pain and Disappointment",
    totalSlides: 10,
    slides: [
      // SLIDE 1 - Title
      {
        type: "title",
        title: "Endurance, Forgiveness & Resilience",
        subtitle: "How Godly Marriages Survive Seasons of Pain and Disappointment",
        scripture: "\"Therefore what God has joined together, let not man separate.\" — Matthew 19:6",
        highlight: "Marriage is not sustained by feelings. Marriage is sustained by COVENANT COMMITMENT. Feelings fluctuate. Covenant remains."
      },
      // SLIDE 2 - Core Truth
      {
        type: "content",
        title: "The Truth About Marital Seasons",
        points: [
          "Every marriage will experience seasons where love feels costly.",
          "Many marriages collapse because couples confuse: Temporary discomfort with permanent failure, emotional fatigue with incompatibility, disappointment with divine disapproval.",
          "Marriage has seasons: Growth, waiting, pressure, pain, restoration.",
          "Healthy couples do not PANIC in hard seasons."
        ],
        scripture: "\"To everything there is a season, a time for every purpose under heaven.\" — Ecclesiastes 3:1"
      },
      // SLIDE 3 - Emotional Decision-Making
      {
        type: "content",
        title: "The Danger of Emotional Decision-Making",
        points: [
          "CULTURAL LIE: 'If I am unhappy, something is wrong.'",
          "KINGDOM TRUTH: Unhappiness is a SIGNAL—not a verdict.",
          "Emotions are INDICATORS, not commanders.",
          "If you faint in the day of adversity, your strength is small.",
          "Endurance must be BUILT before adversity arrives."
        ],
        scripture: "\"If you faint in the day of adversity, your strength is small.\" — Proverbs 24:10"
      },
      // SLIDE 4 - Forgiveness Core Skill
      {
        type: "content",
        title: "Forgiveness: The Core Skill of Marriage",
        points: [
          "Forgiveness is not optional in marriage. It is DAILY work.",
          "Marriage without forgiveness becomes: Bitter, cold, transactional, unsafe.",
          "Forgiveness does not deny pain. Forgiveness RELEASES debt.",
          "Jesus said: Not seven times, but seventy times seven.",
          "He was teaching POSTURE, not mathematics."
        ],
        scripture: "\"Be kind to one another, tenderhearted, forgiving one another, even as God in Christ forgave you.\" — Ephesians 4:32"
      },
      // SLIDE 5 - What Forgiveness Is
      {
        type: "content",
        title: "What Forgiveness IS and IS NOT",
        points: [
          "✅ Forgiveness IS: A decision, an act of obedience, a release of emotional debt, sometimes a process (repeated).",
          "❌ Forgiveness IS NOT: Pretending nothing happened, trusting immediately, removing consequences, silencing pain.",
          "Forgiveness means: 'I release my right to punish you. I will not hold this over you.'"
        ],
        scripture: "\"I, even I, am He who blots out your transgressions for My own sake; and I will not remember your sins.\" — Isaiah 43:25"
      },
      // SLIDE 6 - Resilience
      {
        type: "content",
        title: "Resilience: The Marriage Muscle",
        points: [
          "Marital resilience is the ability to: Recover from conflict, remain connected during stress, adapt without breaking covenant, grow STRONGER after difficulty.",
          "Tribulation produces perseverance; perseverance, character; character, hope.",
          "Marriage matures through PRESSURE, not avoidance."
        ],
        scripture: "\"Tribulation produces perseverance; and perseverance, character; and character, hope.\" — Romans 5:3-4"
      },
      // SLIDE 7 - Pressure Points
      {
        type: "content",
        title: "Common Pressure Points That Test Endurance",
        points: [
          "💰 Financial strain",
          "🙏 Delayed prayers (children, healing, breakthrough)",
          "💼 Career pressure and transitions",
          "🏥 Health challenges",
          "👨‍👩‍👧 Family interference",
          "😔 Emotional exhaustion and spiritual dryness",
          "These do not indicate FAILURE. They indicate FORMATION."
        ],
        scripture: "\"Count it all joy when you fall into various trials, knowing that the testing of your faith produces patience.\" — James 1:2-3"
      },
      // SLIDE 8 - Building Daily Endurance
      {
        type: "content",
        title: "Building Daily Marital Endurance",
        points: [
          "🙏 PRAY TOGETHER: Nothing builds endurance like shared intercession.",
          "💬 COMMUNICATE OPENLY: Don't let distance grow in hard seasons.",
          "🤝 APOLOGIZE QUICKLY: Short accounts prevent deep wounds.",
          "💕 EXTEND GRACE: Especially when it's not deserved.",
          "✌️ CHOOSE PEACE: Over pride, every time.",
          "Endurance is built DAILY, not during crisis."
        ],
        scripture: "\"Bear one another's burdens, and so fulfill the law of Christ.\" — Galatians 6:2"
      },
      // SLIDE 9 - Reflection
      {
        type: "reflection",
        title: "Resilience Self-Assessment",
        questions: [
          "How do I respond when disappointed?",
          "Do I withdraw, attack, or endure?",
          "What patterns did I observe in my parents' marriage?",
          "How do I handle unmet expectations?",
          "Am I willing to forgive repeatedly?"
        ],
        instruction: "Your answers reveal patterns. Patterns can be changed with awareness, intentionality, and God's help."
      },
      // SLIDE 10 - Commitment
      {
        type: "action",
        title: "Our Endurance Covenant",
        challenge: "Declare together: 'We commit to enduring together, forgiving freely, growing through pressure, and honoring our covenant in EVERY season.'",
        nextStep: "Discuss one difficult season you've already faced. What did you learn? How did you grow? What will you do differently next time?",
        scripture: "\"Love bears all things, believes all things, hopes all things, endures all things.\" — 1 Corinthians 13:7",
        closing: "Every great marriage has walked through fire. The fire doesn't destroy you—it refines you."
      }
    ]
  },

  // ============================================================
  // MODULE 10: BOUNDARIES, COMMUNITY & SUSTAINABILITY
  // ============================================================
  10: {
    moduleNumber: 10,
    moduleName: "Boundaries, Community & Sustainability",
    factorName: "Protecting Marriage from External Pressure and Future Breakdown",
    totalSlides: 10,
    slides: [
      // SLIDE 1 - Title
      {
        type: "title",
        title: "Boundaries, Community & Sustainability",
        subtitle: "Protecting Marriage from External Pressure and Future Breakdown",
        scripture: "\"Keep your heart with all diligence, for out of it spring the issues of life.\" — Proverbs 4:23",
        highlight: "A strong marriage is not only built internally—it is GUARDED externally. Love builds marriage. Wisdom PROTECTS it."
      },
      // SLIDE 2 - Core Truth
      {
        type: "content",
        title: "Why External Protection Matters",
        points: [
          "Many couples enter marriage spiritually sincere—but structurally UNPROTECTED.",
          "Many marriages fail because: Boundaries were never defined, community was rejected, isolation was mistaken for privacy.",
          "Marriage requires ACTIVE guarding, not passive hope.",
          "'We don't need anyone else' is a dangerous myth that precedes most major marital collapses."
        ],
        scripture: "\"A prudent man foresees evil and hides himself, but the simple pass on and are punished.\" — Proverbs 22:3"
      },
      // SLIDE 3 - What Boundaries Are
      {
        type: "content",
        title: "Boundaries: The Fence That Protects Love",
        points: [
          "Boundaries are: Loving limits, clear lines of responsibility, protection for covenant—not restriction of freedom.",
          "Boundaries say: 'This marriage is SACRED and must be preserved.'",
          "Boundaries are NOT: Control, punishment, isolation, or secrecy.",
          "Good fences make good neighbors—and good marriages."
        ],
        scripture: "\"Above all else, guard your heart, for everything you do flows from it.\" — Proverbs 4:23 (NIV)"
      },
      // SLIDE 4 - Core Boundary Areas
      {
        type: "content",
        title: "Core Boundary Areas in Marriage",
        points: [
          "👨‍👩‍👧 EXTENDED FAMILY: Marriage creates a new primary family. Parents become honored—but secondary.",
          "👥 FRIENDSHIPS: No emotional dependence outside marriage. No opposite-sex intimacy without transparency.",
          "📱 DIGITAL: Transparency with devices. No hidden accounts or secret conversations.",
          "⏰ TIME: Marriage requires intentional priority. Work, friends, and church must not outrank spouse."
        ],
        scripture: "\"Therefore a man shall leave his father and mother and be joined to his wife.\" — Genesis 2:24"
      },
      // SLIDE 5 - Extended Family
      {
        type: "content",
        title: "Extended Family Boundaries",
        points: [
          "LEAVING does not mean dishonoring. It means REORDERING priority.",
          "Common errors: Parents acting as decision-makers, one spouse siding with family against spouse, oversharing marital conflict with relatives.",
          "Marriage creates a new primary family.",
          "Parents are to be honored—but no longer the primary influence."
        ],
        scripture: "\"Honor your father and your mother.\" — Exodus 20:12 | \"A man shall leave his father and mother.\" — Genesis 2:24"
      },
      // SLIDE 6 - Community
      {
        type: "content",
        title: "Community: God's Design for Marital Safety",
        points: [
          "Isolation WEAKENS marriages. Community STRENGTHENS them.",
          "Why community matters: Marriage blind spots are real, pressure distorts judgment, accountability preserves humility.",
          "Every marriage should have: Spiritual authority, trusted mentors, godly counsel access, safe correction channels.",
          "A threefold cord is not quickly broken."
        ],
        scripture: "\"And let us consider one another in order to stir up love and good works, not forsaking the assembling of ourselves together.\" — Hebrews 10:24-25"
      },
      // SLIDE 7 - Sustainability Practices
      {
        type: "content",
        title: "Long-Term Sustainability Practices",
        points: [
          "📅 DAILY: Prayer together, honest communication, quick forgiveness, mutual honor.",
          "📆 SEASONAL: Marriage check-ins, counseling tune-ups, retreats and rest, vision realignment.",
          "🆘 CRISIS: Seek help early, don't hide struggles, submit to counsel, protect covenant first.",
          "Prevention is easier than repair."
        ],
        scripture: "\"Two are better than one... if they fall, one will lift up his fellow.\" — Ecclesiastes 4:9-10"
      },
      // SLIDE 8 - Warning Signs
      {
        type: "content",
        title: "Warning Signs of Weak Boundaries",
        points: [
          "🚩 Excessive external influence on decisions",
          "🚩 Private emotional outlets outside marriage",
          "🚩 Resistance to accountability or counsel",
          "🚩 Chronic secrecy in any area",
          "🚩 Technology competing with intimacy",
          "These must be addressed before they become crises."
        ],
        scripture: "\"Can a man take fire to his bosom, and his clothes not be burned?\" — Proverbs 6:27"
      },
      // SLIDE 9 - Reflection
      {
        type: "reflection",
        title: "Marriage Protection Plan",
        questions: [
          "What are our boundary commitments?",
          "Who is our accountability structure?",
          "What community support do we have?",
          "What is our conflict escalation plan?",
          "What is our spiritual rhythm together?"
        ],
        instruction: "Create your Marriage Protection Charter together. Write it down. Review it annually."
      },
      // SLIDE 10 - Commitment
      {
        type: "action",
        title: "Our Final Covenant Commitment",
        challenge: "Declare together: 'We commit to GUARDING our marriage, honoring godly boundaries, walking in healthy community, and sustaining our covenant for a LIFETIME—by God's grace and wisdom.'",
        nextStep: "Identify: (1) One mentor couple, (2) Your pastoral connection, (3) Your emergency help plan. Write these down and share contact info.",
        scripture: "\"Unless the LORD guards the city, the watchman stays awake in vain.\" — Psalm 127:1",
        closing: "Marriage is not sustained by emotion, compatibility, or circumstances. It is sustained by COVENANT, DISCIPLINE, WISDOM, and GOD'S GRACE. You are now equipped. Go build something beautiful."
      }
    ]
  }
};

export default INTENSIVE_TEACHING;